import { ProjectJobUpdateReq } from 'domain/payload/request/project/ProjectJobUpdateReq';
import { ProjectJobDetailResp } from 'domain/payload/response/project/ProjectJobDetailResp';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ProjectService from '../../service/ProjectService';
import CustomDropdown from 'component/CustomDropdown';

interface ProjectJobEditModalProps {
  jobId: string;
  onClose: (isRefresh?: boolean) => void;
}

const ProjectJobEditModal: React.FC<ProjectJobEditModalProps> = ({ jobId, onClose }) => {
  const { profile } = useSelector((state: any) => state.auth);
  const projectService = ProjectService();

  const [form, setForm] = useState<ProjectJobUpdateReq>(new ProjectJobUpdateReq());
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDetail();
  }, [jobId]);

  const fetchDetail = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsLoading(true);
    try {
      const res = await projectService.projectJobDetail(token, jobId);
      const detail: ProjectJobDetailResp = res.data!;

      setForm({
        id: detail.id,
        name: detail.name,
        description: detail.description,
        formId: detail.formId,
        maxInput: detail.maxInput,
        placeId: (detail as any).placeId || '',
        areaId: (detail as any).areaId || '',
        projectName: detail.projectName,
        type: detail.type,
        instruction: detail.instruction || '',
        additionalInfo: detail.additionalInfo || '',
        groupName: detail.groupName || '',
        isRunning: detail.isRunning,
        lockSubmitDistance: detail.lockSubmitDistance,
        webFormEligible: detail.webFormEligible || false,
        requireAudioRecording: detail.requireAudioRecording || (detail as any).require_audio_recording || false,
        editableLocation: detail.editableLocation || (detail as any).editable_location || false,
      });
    } catch (err: any) {
      console.error(err);
      window.alert(`Error: ${err}`);
      onClose(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setForm((prev) => ({ ...prev, [name]: Number(value) }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    if (form.additionalInfo && form.additionalInfo.trim() !== '') {
      try {
        JSON.parse(form.additionalInfo);
      } catch {
        setError('Additional Info harus berupa JSON yang valid');
        return;
      }
    }

    setError(null);
    setIsSubmitting(true);
    try {
      const payload: ProjectJobUpdateReq = {
        ...form,
        placeId: form.placeId || undefined,
        areaId: form.areaId || undefined,
      };

      await projectService.projectJobUpdate(token, payload);
      window.alert('Job berhasil diupdate');
      onClose(true);
    } catch (err: any) {
      console.error(err);
      window.alert(`Error: ${err}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="modal d-block"
      tabIndex={-1}
      style={{
        background: 'rgba(0,0,0,0.5)',
        overflowY: 'auto',
        paddingTop: '2rem',
        paddingBottom: '2rem'
      }}
    >
      <div className="modal-dialog modal-lg" style={{ margin: '0 auto' }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Edit Job</h5>
            <button type="button" className="btn-close" onClick={() => onClose(false)} />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
              {isLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status" />
                </div>
              ) : (
                <>
                  {error && <div className="alert alert-danger">{error}</div>}

                  <div className="mb-3">
                    <label className="form-label">Project Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.projectName}
                      disabled
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      name="description"
                      rows={3}
                      value={form.description}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Form ID</label>
                      <input
                        type="text"
                        className="form-control"
                        name="formId"
                        value={form.formId}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Max Input</label>
                      <input
                        type="number"
                        className="form-control"
                        name="maxInput"
                        value={form.maxInput}
                        onChange={handleChange}
                        min={0}
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Place ID</label>
                      <input
                        type="text"
                        className="form-control"
                        name="placeId"
                        value={form.placeId || ''}
                        onChange={handleChange}
                        placeholder="Kosongkan jika menggunakan Area"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Area ID</label>
                      <input
                        type="text"
                        className="form-control"
                        name="areaId"
                        value={form.areaId || ''}
                        onChange={handleChange}
                        placeholder="Kosongkan jika menggunakan Place"
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Type</label>
                      <CustomDropdown
                        value={form.type}
                        options={[
                          { id: 0, name: 'Type 0' },
                          { id: 1, name: 'Type 1' },
                          { id: 2, name: 'Type 2' },
                          { id: 3, name: 'Type 3' }
                        ]}
                        onChange={(option) => {
                          setForm({
                            ...form,
                            type: Number(option.id)
                          });
                        }}
                        align="right"
                        containerClassName="w-100"
                        buttonClassName="form-control w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Group Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="groupName"
                        value={form.groupName}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Instruction (HTML)</label>
                    <textarea
                      className="form-control"
                      name="instruction"
                      rows={5}
                      value={form.instruction}
                      onChange={handleChange}
                      placeholder="<p>Instruksi dalam format HTML</p>"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Additional Info (JSON)</label>
                    <textarea
                      className="form-control font-monospace"
                      name="additionalInfo"
                      rows={4}
                      value={form.additionalInfo}
                      onChange={handleChange}
                      placeholder='{"key":"value"}'
                    />
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          name="isRunning"
                          checked={form.isRunning}
                          onChange={handleChange}
                          id="isRunningSwitch"
                        />
                        <label className="form-check-label" htmlFor="isRunningSwitch">
                          Is Running
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          name="lockSubmitDistance"
                          checked={form.lockSubmitDistance}
                          onChange={handleChange}
                          id="lockSubmitDistanceSwitch"
                        />
                        <label className="form-check-label" htmlFor="lockSubmitDistanceSwitch">
                          Lock Submit Distance
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          name="webFormEligible"
                          checked={form.webFormEligible || false}
                          onChange={handleChange}
                          id="webFormEligibleSwitch"
                        />
                        <label className="form-check-label" htmlFor="webFormEligibleSwitch">
                          Web Form Eligible
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          name="requireAudioRecording"
                          checked={form.requireAudioRecording || false}
                          onChange={handleChange}
                          id="requireAudioRecordingSwitch"
                        />
                        <label className="form-check-label" htmlFor="requireAudioRecordingSwitch">
                          Require Audio Recording
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          name="editableLocation"
                          checked={form.editableLocation || false}
                          onChange={handleChange}
                          id="editableLocationSwitch"
                        />
                        <label className="form-check-label" htmlFor="editableLocationSwitch">
                          Editable Location
                        </label>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => onClose(false)}
                disabled={isSubmitting}
              >
                Batal
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Menyimpan...
                  </>
                ) : (
                  'Simpan'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProjectJobEditModal;